import { useTranslation } from '@kostbar/i18n'
import {
  DAY_KEYS,
  FULL_DAY_CLOSE,
  FULL_DAY_OPEN,
  isFullDay,
} from '../lib/scheduling'
import type { DayKey, RestaurantHours } from '@/api/types'
import { Input } from '@/components/ui/input'
import { Checkbox } from '@/components/ui/checkbox'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'

interface Props {
  title: string
  hint?: string
  value: RestaurantHours
  onChange: (next: RestaurantHours) => void
}

export function HoursForm({ title, hint, value, onChange }: Props) {
  const { t: tRestaurants } = useTranslation('restaurants')

  const update = (d: DayKey, patch: Partial<RestaurantHours[DayKey]>) => {
    onChange({ ...value, [d]: { ...value[d], ...patch } })
  }

  const copyFromMonday = () => {
    const src = value.mon
    const next = { ...value }
    ;(['tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as Array<DayKey>).forEach(
      (d) => (next[d] = { ...src }),
    )
    onChange(next)
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4 space-y-0">
        <div className="space-y-1.5">
          <CardTitle>{title}</CardTitle>
          {hint && <CardDescription>{hint}</CardDescription>}
        </div>
        <Button variant="outline" size="sm" onClick={copyFromMonday}>
          {tRestaurants('scheduling.hours.copyFromMonday')}
        </Button>
      </CardHeader>
      <CardContent>
        <div className="divide-y">
          {DAY_KEYS.map((d) => {
            const day = value[d]
            const fullDay = isFullDay(day)
            return (
              <div
                key={d}
                className="flex min-h-12 flex-wrap items-center gap-x-4 gap-y-2 py-2"
              >
                <div className="w-10 shrink-0 font-medium">
                  {tRestaurants(`scheduling.hours.day.${d}` as const)}
                </div>
                <label className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Checkbox
                    checked={day.closed}
                    onCheckedChange={(v) => update(d, { closed: Boolean(v) })}
                  />
                  {tRestaurants('scheduling.hours.closed')}
                </label>
                {!day.closed && (
                  <div className="ml-auto flex items-center gap-2">
                    {fullDay ? (
                      <>
                        <Badge variant="secondary">
                          {tRestaurants('scheduling.hours.fullDay')}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            update(d, { open: '11:00', close: '21:00' })
                          }
                        >
                          {tRestaurants('scheduling.hours.setTimes')}
                        </Button>
                      </>
                    ) : (
                      <>
                        <Input
                          type="time"
                          value={day.open ?? ''}
                          onChange={(e) => update(d, { open: e.target.value })}
                          className="w-28"
                        />
                        <span className="text-muted-foreground">–</span>
                        <Input
                          type="time"
                          value={day.close ?? ''}
                          onChange={(e) => update(d, { close: e.target.value })}
                          className="w-28"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            update(d, {
                              open: FULL_DAY_OPEN,
                              close: FULL_DAY_CLOSE,
                            })
                          }
                        >
                          {tRestaurants('scheduling.hours.fullDayAction')}
                        </Button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}
